import { useCallback, useMemo } from 'react';

import { useServerStorage } from 'utils/useServerStorage';

type UseFavoriteAgentsResult = {
  readonly favoriteAgentIds: readonly string[];
  readonly addFavorite: (agentId: string) => void;
  readonly removeFavorite: (agentId: string) => void;
  readonly isFavorite: (agentId: string) => boolean;
};

export const useFavoriteAgents = (): UseFavoriteAgentsResult => {
  const storage = useServerStorage('user');

  const favoriteAgentIds = useMemo(
    () => storage.data.team?.favoriteAgentIds ?? [],
    [storage.data.team?.favoriteAgentIds],
  );

  const addFavorite = useCallback(
    (agentId: string) => {
      storage.set({
        team: (draft) => {
          const ids = draft.favoriteAgentIds ?? [];
          if (!ids.includes(agentId)) {
            draft.favoriteAgentIds = [...ids, agentId];
          }
        },
      });
    },
    [storage],
  );

  const removeFavorite = useCallback(
    (agentId: string) => {
      storage.set({
        team: (draft) => {
          const ids = draft.favoriteAgentIds ?? [];
          draft.favoriteAgentIds = ids.filter((id) => id !== agentId);
        },
      });
    },
    [storage],
  );

  const isFavorite = useCallback(
    (agentId: string) => favoriteAgentIds.includes(agentId),
    [favoriteAgentIds],
  );

  return useMemo(
    () => ({
      favoriteAgentIds,
      addFavorite,
      removeFavorite,
      isFavorite,
    }),
    [favoriteAgentIds, addFavorite, removeFavorite, isFavorite],
  );
};
